import React, { useState, useEffect } from 'react';
import './publish.css';
import { FaClock } from 'react-icons/fa';
import { IoMdDownload } from 'react-icons/io';
import PublishHeader from '../../components/header/PublishHeader';
import VideoPlayer from '../../components/video/videoPlayer';
import { useLocation, useNavigate } from 'react-router-dom';
import { abortStitch } from '../../services/canvasVideoBlobService.ts';
import {
  PUBLISH_JOB_STAGE_LABELS,
  PublishJob,
} from '../../types/publishJob.ts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import {
  publishVideoToYouTube,
  YouTubePrivacyStatus,
} from '../../services/youtubeService';

interface PublishProps {
  blob: Blob | null;
  setBlob: (blob: Blob | null) => void;
  publishJob: PublishJob;
}

const MIN_SCHEDULE_LEAD_TIME_MS = 60 * 1000;

const getMinimumScheduleDateTime = () =>
  new Date(Date.now() + MIN_SCHEDULE_LEAD_TIME_MS);

const getDefaultScheduleDateTime = () => {
  const date = new Date(Date.now() + 2 * MIN_SCHEDULE_LEAD_TIME_MS);
  date.setSeconds(0, 0);
  return date;
};

const isSameCalendarDate = (first: Date, second: Date) =>
  first.getFullYear() === second.getFullYear() &&
  first.getMonth() === second.getMonth() &&
  first.getDate() === second.getDate();

const getScheduleDateTime = (date: Date | null, time: Date | null) => {
  if (!date || !time) return null;

  const publishAt = new Date(date);
  publishAt.setHours(
    time.getHours(),
    time.getMinutes(),
    time.getSeconds(),
    0
  );

  return publishAt;
};

const PublishPage: React.FC<PublishProps> = ({
  blob,
  setBlob,
  publishJob,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const title = location.state?.title || 'Untitled Video';
  const initialError = location.state?.error || null;

  const [fileName, setFileName] = useState<string>(title);
  const [fileExtension, setFileExtension] = useState<string>('mp4');
  const [selectedResolution, setSelectedResolution] =
    useState<string>('HD 720p');
  const [youtubeTitle, setYoutubeTitle] = useState<string>(title);
  const [youtubeDescription, setYoutubeDescription] = useState<string>('');
  const [youtubeTag, setYoutubeTag] = useState<string>('');
  const [youtubePrivacy, setYoutubePrivacy] =
    useState<YouTubePrivacyStatus>('public');
  const [youtubeMadeForKids, setYoutubeMadeForKids] =
    useState<boolean>(false);
  const [activeSidebar, setActiveSidebar] = useState<
    'save' | 'youtube' | 'schedule'
  >('save');
  // Truth-driven loading state (no fake timers):
  // - blob present -> ready
  // - job error (or navigation error) -> error panel
  // - preparing/working -> honest indeterminate preparing UI
  // - otherwise (refresh / direct visit, state lost) -> empty state
  const preparing = Boolean(location.state?.preparing);
  const jobError = publishJob.status === 'error' ? publishJob.error : null;
  const displayError = jobError || initialError;
  const jobWorking = publishJob.status === 'working';
  const stalePreparing = preparing && !blob && !jobWorking && !displayError;
  const showEmpty =
    (!blob && !preparing && !jobWorking && !displayError) || stalePreparing;
  const isLoading = !blob && !displayError && !showEmpty;
  const stageLabel = publishJob.stage
    ? PUBLISH_JOB_STAGE_LABELS[publishJob.stage]
    : 'Preparing your video…';

  const handleCancelJob = () => {
    abortStitch();
    navigate(-1);
  };

  const handleBackToEditor = () => {
    navigate(-1);
  };
  
  // Update file extension based on blob type
  useEffect(() => {
    if (blob) {
      if (blob.type.includes('mp4')) {
        setFileExtension('mp4');
      } else if (blob.type.includes('webm')) {
        setFileExtension('webm');
      } else {
        // Default to mp4 for backend-stitched videos
        setFileExtension('mp4');
      }
    }
  }, [blob]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    getDefaultScheduleDateTime
  );
  const [selectedTime, setSelectedTime] = useState<Date | null>(
    getDefaultScheduleDateTime
  );
  const [publishNowSelected, setPublishNowSelected] =
    useState<boolean>(false);
  const [error, setError] = useState<string | null>(initialError);
  const [isPublishingToYoutube, setIsPublishingToYoutube] =
    useState<boolean>(false);
  const [youtubeUploadProgress, setYoutubeUploadProgress] =
    useState<number>(0);
  const [youtubePublishMessage, setYoutubePublishMessage] = useState<
    string | null
  >(null);
  const [youtubePublishedUrl, setYoutubePublishedUrl] = useState<string | null>(
    null
  );
  const [youtubeStudioUrl, setYoutubeStudioUrl] = useState<string | null>(null);

  const handleDownload = () => {
    if (!blob) {
      setError(
        'No video available to download. Please try processing the video again.'
      );
      console.error('No blob available for download.');
      return;
    }
    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.${fileExtension}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download video. Please try again.');
      console.error('Download error:', err);
    }
  };

  const getYoutubeTags = () =>
    Array.from(
      new Set(
        youtubeTag
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean)
      )
    );

  const normalizeScheduleSelection = (
    date: Date | null,
    time: Date | null
  ) => {
    const publishAt = getScheduleDateTime(date, time);

    if (
      publishAt &&
      publishAt.getTime() <= getMinimumScheduleDateTime().getTime()
    ) {
      const nextAvailable = getDefaultScheduleDateTime();
      return { date: nextAvailable, time: nextAvailable };
    }

    return { date, time };
  };

  const handleScheduleDateChange = (newValue: Date | null) => {
    const nextSelection = normalizeScheduleSelection(newValue, selectedTime);

    setSelectedDate(nextSelection.date);
    setSelectedTime(nextSelection.time);
    setPublishNowSelected(false);
  };

  const handleScheduleTimeChange = (newValue: Date | null) => {
    const nextSelection = normalizeScheduleSelection(selectedDate, newValue);

    setSelectedDate(nextSelection.date);
    setSelectedTime(nextSelection.time);
    setPublishNowSelected(false);
  };

  const getScheduledPublishAt = () => {
    if (activeSidebar !== 'schedule' || publishNowSelected) return null;

    const publishAt = getScheduleDateTime(selectedDate, selectedTime);
    return publishAt &&
      publishAt.getTime() > getMinimumScheduleDateTime().getTime()
      ? publishAt
      : null;
  };

  const handlePublishToYoutube = async () => {
    const trimmedTitle = youtubeTitle.trim() || fileName.trim() || title;

    if (!blob) {
      setError('No video is ready to publish yet.');
      return;
    }

    if (!trimmedTitle) {
      setError('Please add a YouTube title before publishing.');
      return;
    }

    const publishAt = getScheduledPublishAt();

    if (activeSidebar === 'schedule' && !publishNowSelected && !publishAt) {
      setError(
        'Choose a future publish date and time, or use Now to publish immediately.'
      );
      return;
    }

    try {
      setError(null);
      setYoutubePublishedUrl(null);
      setYoutubeStudioUrl(null);
      setYoutubeUploadProgress(0);
      setYoutubePublishMessage('Connecting to YouTube...');
      setIsPublishingToYoutube(true);

      const result = await publishVideoToYouTube({
        blob,
        title: trimmedTitle,
        description: youtubeDescription.trim(),
        tags: getYoutubeTags(),
        privacyStatus: youtubePrivacy,
        madeForKids: youtubeMadeForKids,
        publishAt,
        onProgress: setYoutubeUploadProgress,
      });

      setYoutubeUploadProgress(100);
      setYoutubePublishedUrl(result.url);
      setYoutubeStudioUrl(result.studioUrl);
      const channelText = result.channelTitle
        ? ` on ${result.channelTitle}`
        : '';
      const visibilityText = result.privacyStatus ?? youtubePrivacy;
      setYoutubePublishMessage(
        publishAt
          ? `Uploaded and scheduled on YouTube${channelText}. YouTube may keep processing it for a few minutes.`
          : visibilityText === 'public'
            ? `Uploaded to YouTube${channelText}. YouTube is processing the video, so playback and quality may take a few minutes.`
            : `Uploaded to YouTube${channelText} as ${visibilityText}. YouTube is processing it, and it will not appear publicly unless visibility is Public.`
      );
    } catch (err) {
      setYoutubePublishMessage(null);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to publish to YouTube. Please try again.'
      );
    } finally {
      setIsPublishingToYoutube(false);
    }
  };

  const platforms = [
    { name: 'YouTube', icon: '🔴', requestPlatform: false },
    { name: 'Instagram', icon: '📷', requestPlatform: true },
    { name: 'Facebook', icon: '📘', requestPlatform: true },
    { name: 'LG AVOD', icon: '📺', requestPlatform: true },
    { name: 'LG FAST', icon: '📺', requestPlatform: true },
    { name: 'Amazon channels', icon: '📦', requestPlatform: true },
  ];

  const handlePlatformClick = (platformName: string) => {
    if (platformName === 'YouTube') {
      setActiveSidebar('youtube');
    }
  };

  const pickerStyle = {
    textField: {
      className: 'schedule-picker-field',
      sx: {
        width: '100%',
        backgroundColor: '#20212a',
        borderRadius: '8px',
        '& .MuiInputLabel-root, & .MuiFormLabel-root': {
          color: '#bdb7cf',
          fontFamily: 'Poppins',
          fontWeight: 700,
          fontSize: '14px',
        },
        '& .MuiInputLabel-root.Mui-focused, & .MuiFormLabel-root.Mui-focused':
          {
            color: '#36cedc',
          },
        '& .MuiInputBase-root, & .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root':
          {
            minHeight: '58px',
            color: '#f9f7ff',
            backgroundColor: '#20212a',
            borderRadius: '8px',
          },
        '& .MuiInputBase-input, & input': {
          color: '#f9f7ff',
          fontFamily: 'Poppins',
          fontWeight: 800,
          fontSize: '16px',
        },
        '& .MuiPickersSectionList-root, & .MuiPickersSectionList-sectionContent, & .MuiPickersSectionList-sectionSeparator':
          {
            color: '#f9f7ff !important',
            fontFamily: 'Poppins',
            fontWeight: 800,
            fontSize: '16px',
          },
        '& .MuiPickersSectionList-root': {
          padding: '18px 0',
        },
        '& .MuiInputBase-input::placeholder, & input::placeholder': {
          color: '#8f879f',
          opacity: 1,
        },
        '& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline':
          {
            borderColor: 'rgba(170, 147, 198, 0.42)',
          },
        '&:hover .MuiOutlinedInput-notchedOutline, &:hover .MuiPickersOutlinedInput-notchedOutline':
          {
            borderColor: 'rgba(170, 147, 198, 0.64)',
          },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline':
          {
            borderColor: '#36cedc',
          },
        '& .MuiOutlinedInput-root.Mui-focused, & .MuiPickersOutlinedInput-root.Mui-focused':
          {
            boxShadow: '0 0 0 3px rgba(54, 206, 220, 0.14)',
          },
        '& .MuiSvgIcon-root, & .MuiIconButton-root': {
          color: '#aa93c6',
        },
        '& .MuiIconButton-root:hover': {
          backgroundColor: 'rgba(170, 147, 198, 0.12)',
        },
        '& .MuiPickersSectionList-sectionContent.Mui-selected': {
          color: '#36cedc',
        },
      },
    },
    popper: {
      className: 'schedule-picker-popper',
      placement: 'bottom-start' as const,
    },
    desktopPaper: {
      className: 'schedule-picker-paper',
    },
    actionBar: {
      className: 'schedule-picker-action-bar',
    },
  };

  const renderSaveOptions = () => {
    return (
      <div className="save-section">
        <div className="save-section-header">
          <h2>Save as</h2>
          <div className="filename-input">
            <input
              type="text"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
            />
            <span className="file-extension">.{fileExtension}</span>
          </div>
        </div>

        <div className="save-section-flex">
          <h2>Save File</h2>
          <button
            className="download-btn"
            onClick={handleDownload}
            disabled={!blob}
          >
            <IoMdDownload /> Download Video
          </button>
        </div>
        {(displayError || showEmpty) && (
          <div className="publish-status-message error">
            {displayError ||
              'No video is ready yet. Please wait or go back to the editor.'}
          </div>
        )}
        <div className="save-section-flex">
          <h3>Publish to</h3>
          <div className="platforms-list">
            {platforms.map((platform, index) => (
              <div
                key={index}
                className="platform-item"
                onClick={() => handlePlatformClick(platform.name)}
              >
                <div className="platform-info">
                  <div className="platform-icon">
                    {platform.name === 'YouTube' ? (
                      <svg
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                        fill="red"
                      >
                        <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"></path>
                      </svg>
                    ) : platform.name === 'Instagram' ? (
                      <svg
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                        fill="#E1306C"
                      >
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"></path>
                      </svg>
                    ) : platform.name === 'Facebook' ? (
                      <svg
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                        fill="#1877F2"
                      >
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385h-3.047v-3.47h3.047v-2.644c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953h-1.514c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385c5.737-.9 10.125-5.864 10.125-11.854z"></path>
                      </svg>
                    ) : (
                      <div className="generic-icon">
                        {platform.name.includes('LG') ? (
                          <svg
                            viewBox="0 0 24 24"
                            width="24"
                            height="24"
                            fill="#A50034"
                          >
                            <circle cx="12" cy="12" r="11" />
                            <text
                              x="12"
                              y="16"
                              textAnchor="middle"
                              fill="white"
                              fontSize="10"
                            >
                              LG
                            </text>
                          </svg>
                        ) : (
                          <svg
                            viewBox="0 0 24 24"
                            width="24"
                            height="24"
                            fill="#232F3E"
                          >
                            <rect width="24" height="24" rx="4" />
                            <text
                              x="12"
                              y="16"
                              textAnchor="middle"
                              fill="white"
                              fontSize="8"
                            >
                              AMZ
                            </text>
                          </svg>
                        )}
                      </div>
                    )}
                  </div>
                  <span className="platform-name">{platform.name}</span>
                </div>
                {platform.requestPlatform && (
                  <button className="request-platform-btn">
                    Request Platform
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderScheduleOptions = () => {
    const isSelectedDateToday = selectedDate
      ? isSameCalendarDate(selectedDate, new Date())
      : false;

    return (
      <div className="schedule-section">
        <h2>Schedule publish</h2>

        <div className="date-picker-section">
          <DatePicker
            label="Date"
            disablePast
            minDate={new Date()}
            slotProps={pickerStyle}
            value={selectedDate}
            onChange={handleScheduleDateChange}
          />
        </div>

        <div className="time-picker-section">
          <TimePicker
            label="Time"
            disablePast={isSelectedDateToday}
            minTime={
              isSelectedDateToday ? getMinimumScheduleDateTime() : undefined
            }
            slotProps={pickerStyle}
            value={selectedTime}
            onChange={handleScheduleTimeChange}
          />

          <button
            className="now-btn"
            onClick={() => {
              const now = new Date();
              setSelectedDate(now);
              setSelectedTime(now);
              setPublishNowSelected(true);
              setError(null);
            }}
          >
            <FaClock /> Now
          </button>
        </div>

        <div className="publish-action-btns">
          <button
            className="publish-btnn"
            onClick={handlePublishToYoutube}
            disabled={isPublishingToYoutube || isLoading || !blob}
          >
            {isPublishingToYoutube
              ? `Publishing ${youtubeUploadProgress}%`
              : 'Publish'}
          </button>
          <button
            className="back-btn"
            onClick={() => setActiveSidebar('youtube')}
            disabled={isPublishingToYoutube}
          >
            Back
          </button>
          {(isPublishingToYoutube || youtubePublishMessage) && (
            <div className="youtube-upload-status">
              <div className="youtube-upload-progress-bar">
                <div
                  className="youtube-upload-progress-fill"
                  style={{ width: `${youtubeUploadProgress}%` }}
                />
              </div>
              {youtubePublishMessage && (
                <div className="publish-status-message success">
                  <span>{youtubePublishMessage}</span>
                  {youtubePublishedUrl && (
                    <a
                      href={youtubePublishedUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open watch page
                    </a>
                  )}
                  {youtubeStudioUrl && (
                    <a
                      href={youtubeStudioUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View processing in YouTube Studio
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
          {error && (
            <div className="publish-status-message error">{error}</div>
          )}
        </div>
      </div>
    );
  };

  const openScheduleOptions = () => {
    const nextSelection = normalizeScheduleSelection(selectedDate, selectedTime);

    setSelectedDate(nextSelection.date);
    setSelectedTime(nextSelection.time);
    setPublishNowSelected(false);
    setError(null);
    setActiveSidebar('schedule');
  };

  const renderYoutubeOptions = () => {
    return (
      <div className="youtube-options">
        <div className="youtube-options-header">
          <h2>Export Options</h2>
          <div className="form-group-publish">
            <div className="select-dropdown resolution-select">
              <select
                value={selectedResolution}
                onChange={(e) => setSelectedResolution(e.target.value)}
              >
                <option value="HD 720p">HD 720p</option>
                <option value="HD 1080p">HD 1080p</option>
                <option value="4K">4K</option>
              </select>
              <span className="dropdown-arrow">▼</span>
            </div>
          </div>
        </div>

        <div className="form-group-publish">
          <label>YouTube Title</label>
          <input
            type="text"
            className="youtube-input"
            placeholder="Add title"
            value={youtubeTitle}
            onChange={(e) => setYoutubeTitle(e.target.value)}
          />
        </div>

        <div className="form-group-publish">
          <label>Description</label>
          <textarea
            className="youtube-textarea"
            placeholder="Add a short description (150 words max)"
            value={youtubeDescription}
            onChange={(e) => setYoutubeDescription(e.target.value)}
          />
        </div>

        <div className="form-group-publish">
          <label>Tags</label>
          <div className="tag-input-wrapper">
            <input
              type="text"
              className="youtube-input"
              placeholder="animation, explainer, kids story"
              value={youtubeTag}
              onChange={(e) => setYoutubeTag(e.target.value)}
            />
          </div>
        </div>

        <div className="form-group-publish">
          <label>Visibility</label>
          <div className="select-dropdown resolution-select">
            <select
              value={youtubePrivacy}
              onChange={(e) =>
                setYoutubePrivacy(e.target.value as YouTubePrivacyStatus)
              }
            >
              <option value="private">Private</option>
              <option value="unlisted">Unlisted</option>
              <option value="public">Public</option>
            </select>
            <span className="dropdown-arrow">▼</span>
          </div>
        </div>

        <div className="form-group-publish">
          <label>Audience</label>
          <div className="youtube-radio-group">
            <label className="youtube-radio-option">
              <input
                type="radio"
                name="youtube-made-for-kids"
                checked={youtubeMadeForKids}
                onChange={() => setYoutubeMadeForKids(true)}
              />
              <span>Yes, it's made for kids</span>
            </label>
            <label className="youtube-radio-option">
              <input
                type="radio"
                name="youtube-made-for-kids"
                checked={!youtubeMadeForKids}
                onChange={() => setYoutubeMadeForKids(false)}
              />
              <span>No, it's not made for kids</span>
            </label>
          </div>
        </div>

        <div className="publish-action-btns">
          <button className="next-btn" onClick={openScheduleOptions}>
            Next
          </button>
          <button className="back-btn" onClick={() => setActiveSidebar('save')}>
            Back
          </button>
        </div>
      </div>
    );
  };

  const renderSidebar = () => {
    switch (activeSidebar) {
      case 'save':
        return renderSaveOptions();
      case 'youtube':
        return renderYoutubeOptions();
      case 'schedule':
        return renderScheduleOptions();
      default:
        return renderSaveOptions();
    }
  };

  return (
    <div className="publish-page">
      <PublishHeader clearBlob={() => setBlob(null)} />
      <div className="video-preview-container">
        <div className="main-content-publish">
          {isLoading ? (
            <div className="loading-container">
              <h1>{stageLabel}</h1>
              <div className="progress-container">
                <div className="progress-bar progress-bar-indeterminate">
                  <div className="progress-fill progress-fill-indeterminate"></div>
                </div>
              </div>
              <p className="loading-message">
                Please wait while we finalize your video
              </p>
              <div className="publish-action-btns">
                <button className="back-btn" onClick={handleCancelJob}>
                  Cancel
                </button>
              </div>
            </div>
          ) : displayError ? (
            <div className="error-container">
              <h1>Video processing failed</h1>
              <p className="publish-status-message error">{displayError}</p>
              <div className="publish-action-btns">
                <button className="back-btn" onClick={handleBackToEditor}>
                  Back to editor
                </button>
              </div>
            </div>
          ) : showEmpty ? (
            <div className="error-container">
              <h1>No video to preview</h1>
              <p className="loading-message">
                {stalePreparing
                  ? 'This publish session has expired. Please publish again from the editor.'
                  : 'Open a video in the editor and click Publish to preview it here.'}
              </p>
              <div className="publish-action-btns">
                <button className="back-btn" onClick={handleBackToEditor}>
                  Back to editor
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="preview-video-ready-container">
                <h1 className="preview-video-ready">Preview video ready!</h1>
              </div>
              <div className="video-player-container">
                <VideoPlayer blob={blob} />
              </div>
            </>
          )}
        </div>

        <div className="sidebar-publish">{renderSidebar()}</div>
      </div>
    </div>
  );
};

export default PublishPage;
